import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Home, Search, MessageSquare, User, Inbox, Wallet, LayoutDashboard, Settings, ClipboardList, PartyPopper, Plus, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth, API } from '../App';

/**
 * Mobile Bottom Tab Bar Component
 * 
 * Displays a fixed bottom navigation bar on mobile devices.
 * Hidden on md: breakpoint and above.
 * 
 * Customer Navigation (Event-Centric):
 * Home | Browse | Events (center primary) | Messages | Account
 * 
 * Vendor Navigation:
 * Dashboard | Inquiries | Messages | Payments | Account
 * 
 * @param {Object} props
 * @param {string} props.activeTab - Currently active tab id (maps to dashboard tab values)
 * @param {function} props.onTabChange - Callback when tab changes (for dashboard tabs)
 * @param {string} props.variant - 'customer' | 'vendor'
 */

// Customer tab configuration - Event-centric (5 tabs)
// Home | Browse | Events (center/primary) | Messages | Account
const CUSTOMER_TABS = [
  { 
    id: 'home', 
    label: 'Home', 
    icon: Home,
    path: '/customer-dashboard',
    tabParam: 'welcome',
    badgeKey: null,
    isPrimary: false
  },
  { 
    id: 'browse', 
    label: 'Browse', 
    icon: Search,
    path: '/browse',
    tabParam: null,
    badgeKey: null,
    isPrimary: false
  },
  { 
    id: 'events', 
    label: 'Events', 
    icon: PartyPopper,
    path: '/customer-dashboard',
    tabParam: 'events',
    badgeKey: 'event_count',
    isPrimary: true  // Center emphasized button
  },
  { 
    id: 'messages', 
    label: 'Chats', 
    icon: MessageSquare,
    path: '/customer-dashboard',
    tabParam: 'messages',
    // Role-scoped Badge Counts Fix (2026-06-01): customer Chats badge
    // must NOT include vendor-inbound unread for dual-role accounts.
    badgeKey: 'unread_messages_customer',
    isPrimary: false
  },
  { 
    id: 'account', 
    label: 'Account', 
    icon: User,
    path: '/customer-dashboard',
    tabParam: 'profile',
    badgeKey: null,
    isPrimary: false
  },
];

// Vendor tab configuration (5 tabs) - no URL params, uses internal tab state
// Dashboard | Requests | Schedule (center) | Payments | Account
const VENDOR_TABS = [
  { 
    id: 'dashboard', 
    label: 'Dashboard', 
    icon: LayoutDashboard,
    dashboardTab: 'welcome',
    badgeKey: null
  },
  { 
    id: 'inquiries', 
    label: 'Requests', 
    icon: Inbox,
    dashboardTab: 'inquiries',
    // Role-scoped Badge Counts Fix (2026-06-01): vendor Requests badge
    // must NOT include customer-inbound unread for dual-role accounts.
    badgeKey: 'unread_messages_vendor'
  },
  { 
    id: 'schedule', 
    label: 'Schedule', 
    icon: Calendar,
    dashboardTab: 'calendar', // internal route key preserved
    badgeKey: null
  },
  { 
    id: 'payments', 
    label: 'Payments', 
    icon: Wallet,
    dashboardTab: 'payments',
    badgeKey: null
  },
  { 
    id: 'account', 
    label: 'Account', 
    icon: Settings,
    dashboardTab: 'account',
    badgeKey: null
  },
];

const BottomTabBar = ({ 
  activeTab, 
  onTabChange,
  variant = 'customer' 
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole, activeRole, user } = useAuth();
  
  // Badge counts state
  const [badgeCounts, setBadgeCounts] = useState({
    unread_messages: 0,
    unread_messages_customer: 0,
    unread_messages_vendor: 0,
    vendor_pending_inquiries: 0,
    customer_pending_bookings: 0
  });

  // Fetch badge counts
  const fetchBadgeCounts = useCallback(async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API}/badges/counts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBadgeCounts(response.data);
    } catch (error) {
      // Silently fail - badges are not critical
      console.log('[BottomTabBar] Failed to fetch badge counts:', error.message);
    }
  }, [user]);

  // Store last route on mount for current role
  useEffect(() => {
    if (!user) return;
    
    const pathname = location.pathname;
    const search = location.search;
    const fullPath = pathname + search;
    
    if (variant === 'vendor' && activeRole === 'vendor') {
      // Store vendor route on mount
      if (pathname.startsWith('/vendor/')) {
        localStorage.setItem('celey:lastRoute:vendor', fullPath);
      }
    } else if (variant === 'customer' && activeRole === 'customer') {
      // Store customer route on mount
      if (pathname === '/browse' || pathname.startsWith('/vendors/') || pathname.includes('/customer-dashboard')) {
        localStorage.setItem('celey:lastRoute:customer', fullPath);
      }
    }
  }, [location.pathname, location.search, variant, activeRole, user]);

  // Fetch badge counts on mount, tab change, and periodically
  useEffect(() => {
    fetchBadgeCounts();
    
    // Refresh counts every 30 seconds
    const interval = setInterval(fetchBadgeCounts, 30000);
    
    return () => clearInterval(interval);
  }, [fetchBadgeCounts, activeTab]);

  // Determine which tabs to show based on variant
  const tabs = variant === 'vendor' ? VENDOR_TABS : CUSTOMER_TABS;

  // For vendor variant, check if user actually has vendor role
  // If not, don't render the vendor tab bar
  if (variant === 'vendor' && (!hasRole('vendor') || activeRole !== 'vendor')) {
    return null;
  }

  // Get badge count for a tab
  const getBadgeCount = (tab) => {
    if (!tab.badgeKey) return 0;
    return badgeCounts[tab.badgeKey] || 0;
  };

  // Determine which tab is active based on current location and activeTab prop
  const getActiveTabId = () => {
    const pathname = location.pathname;
    
    // Settings pages are part of account flow
    if (pathname.startsWith('/settings/')) {
      return 'account';
    }
    
    // Event detail pages are part of events flow
    if (pathname.startsWith('/events/') || pathname === '/plan-event') {
      return 'events';
    }
    
    // Vendor dashboard logic
    if (variant === 'vendor' && pathname.includes('/vendor/dashboard')) {
      // Map vendor dashboard tab values to bottom bar tab ids
      if (activeTab === 'welcome') return 'dashboard';
      if (activeTab === 'inquiries') return 'inquiries';
      // Post Chats→Requests merge: stale 'messages' tab value routes to Requests
      if (activeTab === 'messages') return 'inquiries';
      if (activeTab === 'calendar') return 'schedule';
      if (activeTab === 'payments') return 'payments';
      if (activeTab === 'account') return 'account';
      if (activeTab === 'profile') return 'account'; // Profile now accessible via Account
      // Default to dashboard for vendor
      return 'dashboard';
    }
    
    // Customer dashboard logic
    if (pathname === '/browse' || pathname.startsWith('/vendors/')) {
      // Both browse page and vendor detail pages are part of "browse" flow
      return 'browse';
    }
    
    if (pathname.includes('/customer-dashboard')) {
      // Map dashboard tab values to bottom bar tab ids
      if (activeTab === 'welcome') return 'home';
      if (activeTab === 'messages') return 'messages';
      if (activeTab === 'events') return 'events';
      if (activeTab === 'inquiries') return 'home'; // Inquiries now part of home
      if (activeTab === 'profile') return 'account';
      if (activeTab === 'account') return 'account';
      return 'home'; // default
    }
    
    return variant === 'vendor' ? 'dashboard' : 'home';
  };

  const currentTabId = getActiveTabId();

  const handleTabClick = (tab) => {
    // Helper function to store last route per role
    const storeLastRoute = (route) => {
      if (variant === 'vendor') {
        localStorage.setItem('celey:lastRoute:vendor', route);
      } else {
        localStorage.setItem('celey:lastRoute:customer', route);
      }
    };

    // VENDOR TAB HANDLING - no URL params, just internal tab switching
    if (variant === 'vendor') {
      // All vendor tabs stay on /vendor/dashboard
      const vendorRoute = `/vendor/dashboard?tab=${tab.dashboardTab}`;
      storeLastRoute(vendorRoute);
      
      // If already on vendor dashboard and onTabChange is provided, switch tabs
      if (location.pathname.includes('/vendor/dashboard') && onTabChange && tab.dashboardTab) {
        onTabChange(tab.dashboardTab);
        return;
      }
      
      // Otherwise, navigate to vendor dashboard with the tab param
      navigate(vendorRoute, { replace: true });
      return;
    }

    // CUSTOMER TAB HANDLING
    // If clicking browse, navigate to /browse
    if (tab.id === 'browse') {
      storeLastRoute('/browse');
      navigate('/browse', { replace: true });
      return;
    }

    // Build the customer dashboard route with tab param
    const customerRoute = `/customer-dashboard?tab=${tab.tabParam}`;
    storeLastRoute(customerRoute);

    // If on /browse or vendor detail page and clicking another tab, navigate to dashboard with tab param
    if (location.pathname === '/browse' || location.pathname.startsWith('/vendors/')) {
      navigate(customerRoute, { replace: true });
      return;
    }

    // If already on dashboard, just switch tabs via callback
    if (location.pathname.includes('/customer-dashboard')) {
      if (onTabChange && tab.tabParam) {
        onTabChange(tab.tabParam);
      }
      return;
    }

    // Default: navigate to dashboard with tab
    navigate(customerRoute, { replace: true });
  };

  return (
    <>
      {/* Spacer to prevent content from being hidden behind tab bar + safe area */}
      <div className="h-20 md:hidden pb-safe" aria-hidden="true" />
      
      {/* Bottom Tab Bar - Mobile Only */}
      <nav 
        className={cn(
          "fixed bottom-0 inset-x-0 z-50",
          "bg-white/95 backdrop-blur-md border-t border-gray-100",
          "shadow-tab-bar",
          "md:hidden", // Hide on desktop
          "bottom-tab-bar transition-all duration-200" // For keyboard hide animation
        )}
        role="tablist"
        aria-label={variant === 'vendor' ? 'Vendor navigation' : 'Main navigation'}
        data-testid="bottom-tab-bar"
        data-variant={variant}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex justify-around items-stretch h-16">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = currentTabId === tab.id;
            const badgeCount = getBadgeCount(tab);
            const isPrimary = tab.isPrimary;
            
            // Primary center button (Events) has special styling
            if (isPrimary) {
              return (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab)}
                  className={cn(
                    "flex flex-col items-center justify-center flex-1 relative",
                    "transition-all duration-150",
                    "focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-inset"
                  )}
                  role="tab"
                  aria-selected={isActive}
                  aria-label={`${tab.label}${badgeCount > 0 ? `, ${badgeCount} notifications` : ''}`}
                  data-testid={`tab-${tab.id}`}
                >
                  {/* Elevated primary button */}
                  <div className={cn(
                    "relative -mt-5 w-14 h-14 rounded-full flex items-center justify-center",
                    "shadow-lg transition-all duration-200",
                    isActive 
                      ? (variant === 'customer' ? "bg-[#C6A96B] scale-105" : "bg-emerald-600 scale-105")
                      : (variant === 'customer' ? "bg-[#B89655] hover:bg-[#C6A96B]" : "bg-emerald-500 hover:bg-emerald-600")
                  )}>
                    <Icon className="h-6 w-6 text-white" />
                    {/* Badge indicator */}
                    {badgeCount > 0 && (
                      <span 
                        className={cn(
                          "absolute -top-1 -right-1 min-w-[20px] h-[20px] px-1",
                          "flex items-center justify-center",
                          "bg-red-500 text-white text-[10px] font-bold rounded-full",
                          "shadow-sm border-2 border-white"
                        )}
                        aria-hidden="true"
                      >
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </div>
                  <span className={cn(
                    "text-xs mt-1 font-medium",
                    isActive ? (variant === 'customer' ? "text-[#C6A96B]" : "text-emerald-600") : "text-gray-600"
                  )}>
                    {tab.label}
                  </span>
                </button>
              );
            }
            
            // Regular tab buttons
            return (
              <button
                key={tab.id}
                onClick={() => handleTabClick(tab)}
                className={cn(
                  "flex flex-col items-center justify-center flex-1 relative",
                  "transition-colors duration-150",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-inset",
                  isActive 
                    ? (variant === 'customer' ? "text-[#C6A96B]" : "text-emerald-600")
                    : "text-gray-500 active:text-gray-700"
                )}
                role="tab"
                aria-selected={isActive}
                aria-label={`${tab.label}${badgeCount > 0 ? `, ${badgeCount} notifications` : ''}`}
                data-testid={`tab-${tab.id}`}
              >
                {/* Active pill indicator — 3px rounded bar above the icon,
                    rendered only on the active tab. Reinforces the
                    selection without recoloring the entire row. */}
                {isActive && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      'absolute top-1 h-[3px] w-7 rounded-full',
                      variant === 'customer' ? 'bg-[#C6A96B]' : 'bg-emerald-500'
                    )}
                  />
                )}
                <div className="relative">
                  <Icon 
                    className={cn(
                      "h-6 w-6 mb-1",
                      isActive && "stroke-[2.5px]"
                    )} 
                  />
                  {/* Badge indicator */}
                  {badgeCount > 0 && (
                    <span 
                      className={cn(
                        "absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1",
                        "flex items-center justify-center",
                        "bg-red-500 text-white text-[10px] font-bold rounded-full",
                        "shadow-sm"
                      )}
                      aria-hidden="true"
                    >
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </span>
                  )}
                </div>
                <span 
                  className={cn(
                    "text-xs",
                    isActive ? "font-medium" : "font-normal"
                  )}
                >
                  {tab.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default BottomTabBar;
